// Tab "Tipos de Seguro" — renomeia o produto que o segurado lê no checkout
// ("Seguro Pessoas" → "Seguro Vida") sem tocar no enum do core, que vale para
// toda a base.
//
// A lista vem interpolada do core: TODOS os tipos do domínio, com o rótulo em
// vigor e o padrão ao lado. O front não guarda a lista de tipos — foi a cópia
// dessa lista em cada front que motivou esta camada.
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  useAdminWhiteLabelInsuranceTypes,
  useRemoveAdminWhiteLabelInsuranceTypeMutation,
  useUpsertAdminWhiteLabelInsuranceTypeMutation,
} from "@/services/admin"
import type { AdminWhiteLabelInsuranceType } from "@/types/admin-white-labels"

const panelPrefix = "pages.adminWhiteLabels"

type InsuranceTypesTabProps = {
  uuid: string
}

// Guarda SÓ o que o usuário editou. O que não está aqui vem do servidor, então
// não há estado espelhado para ressincronizar depois de salvar ou restaurar.
type DraftMap = Record<
  string,
  { groupName: string; description: string }
>

export const InsuranceTypesTab = ({
  uuid,
}: InsuranceTypesTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabelInsuranceTypes(uuid)
  const upsertMutation =
    useUpsertAdminWhiteLabelInsuranceTypeMutation(uuid)
  const removeMutation =
    useRemoveAdminWhiteLabelInsuranceTypeMutation(uuid)

  const [drafts, setDrafts] = useState<DraftMap>({})

  // Descarta a edição local para o campo voltar a seguir o servidor — é o que
  // faz o texto sumir da tela quando o rótulo é restaurado ao padrão.
  const clearDraft = (code: string) =>
    setDrafts((current) => {
      const { [code]: removed, ...rest } = current
      void removed
      return rest
    })

  if (isLoading) {
    return (
      <div
        data-testid="wl-insurance-types-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={`wl-insurance-types-skeleton-${index}`}
            className="h-20 w-full rounded-xl"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.insuranceTypes.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  // Mesma armadilha do 204: corpo vazio chega como string, não undefined.
  const types = Array.isArray(data) ? data : []
  const isSaving =
    upsertMutation.isPending || removeMutation.isPending

  const draftOf = (type: AdminWhiteLabelInsuranceType) =>
    drafts[type.code] ?? {
      groupName: type.groupName,
      description: type.description,
    }

  const isDirty = (type: AdminWhiteLabelInsuranceType) => {
    const draft = draftOf(type)
    return (
      draft.groupName !== type.groupName ||
      draft.description !== type.description
    )
  }

  // Semeia o draft com o valor do servidor: editar um campo não pode zerar o
  // outro, que continuaria vindo do servidor até o próximo render.
  const handleChange = (
    type: AdminWhiteLabelInsuranceType,
    field: "groupName" | "description",
    value: string
  ) => {
    const current = draftOf(type)
    setDrafts((drafts) => ({
      ...drafts,
      [type.code]: { ...current, [field]: value },
    }))
  }

  const handleSave = (
    type: AdminWhiteLabelInsuranceType
  ) => {
    const draft = draftOf(type)
    upsertMutation.mutate(
      {
        code: type.code,
        payload: {
          groupName: draft.groupName.trim(),
          description: draft.description.trim(),
          active: true,
        },
      },
      { onSuccess: () => clearDraft(type.code) }
    )
  }

  // Desligar preserva o texto (PUT active=false); só o "restaurar padrão"
  // apaga o registro.
  const handleToggle = (
    type: AdminWhiteLabelInsuranceType,
    nextValue: boolean
  ) => {
    const draft = draftOf(type)
    upsertMutation.mutate(
      {
        code: type.code,
        payload: {
          groupName: draft.groupName.trim(),
          description: draft.description.trim(),
          active: nextValue,
        },
      },
      { onSuccess: () => clearDraft(type.code) }
    )
  }

  const renderType = (
    type: AdminWhiteLabelInsuranceType
  ) => {
    const draft = draftOf(type)
    const dirty = isDirty(type)
    const customized = type.source === "WHITE_LABEL"

    return (
      <li
        key={type.code}
        data-testid={`wl-insurance-type-${type.code}`}
        // Fundo na cor da marca destaca o que foge do padrão — o mesmo sinal que
        // o segurado vai ver no checkout sai realçado aqui.
        className={`flex flex-col gap-3 rounded-xl border p-4 shadow-sm ${
          customized
            ? "border-primary/30 bg-primary/5"
            : "bg-card"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{type.code}</Badge>
            {customized ? (
              <Badge variant="secondary">
                {t(
                  `${panelPrefix}.insuranceTypes.customized`
                )}
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">
                {t(
                  `${panelPrefix}.insuranceTypes.usingDefault`
                )}
              </span>
            )}
          </div>

          <Switch
            checked={type.active}
            disabled={isSaving}
            aria-label={t(
              `${panelPrefix}.insuranceTypes.activeLabel`
            )}
            onCheckedChange={(nextValue) =>
              handleToggle(type, nextValue)
            }
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t(`${panelPrefix}.insuranceTypes.nameLabel`)}
            </span>
            <Input
              value={draft.groupName}
              placeholder={type.defaultGroupName}
              disabled={isSaving}
              onChange={(event) =>
                handleChange(
                  type,
                  "groupName",
                  event.target.value
                )
              }
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">
              {t(
                `${panelPrefix}.insuranceTypes.descriptionLabel`
              )}
            </span>
            <Input
              value={draft.description}
              placeholder={type.defaultDescription}
              disabled={isSaving}
              onChange={(event) =>
                handleChange(
                  type,
                  "description",
                  event.target.value
                )
              }
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {t(
              `${panelPrefix}.insuranceTypes.defaultHint`,
              {
                name: type.defaultGroupName,
                description: type.defaultDescription,
              }
            )}
          </span>

          <div className="flex gap-2">
            {customized && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isSaving}
                onClick={() =>
                  removeMutation.mutate(type.code, {
                    onSuccess: () => clearDraft(type.code),
                  })
                }
              >
                {t(`${panelPrefix}.insuranceTypes.restore`)}
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              disabled={
                isSaving ||
                !dirty ||
                !draft.groupName.trim() ||
                !draft.description.trim()
              }
              onClick={() => handleSave(type)}
            >
              {t(`${panelPrefix}.insuranceTypes.save`)}
            </Button>
          </div>
        </div>
      </li>
    )
  }

  // Separa o que a marca personalizou do que segue o padrao: com 15 tipos numa
  // lista unica, o punhado de rotulos proprios se perde no meio.
  const customizedTypes = types.filter(
    (type) => type.source === "WHITE_LABEL"
  )
  const defaultTypes = types.filter(
    (type) => type.source !== "WHITE_LABEL"
  )

  const section = (
    titleKey: string,
    testId: string,
    items: AdminWhiteLabelInsuranceType[]
  ) =>
    items.length > 0 && (
      <section
        data-testid={testId}
        className="flex flex-col gap-3"
      >
        <h3 className="text-sm font-semibold text-muted-foreground">
          {t(`${panelPrefix}.insuranceTypes.${titleKey}`, {
            count: items.length,
          })}
        </h3>
        <ul className="flex flex-col gap-3">
          {items.map(renderType)}
        </ul>
      </section>
    )

  return (
    <div className="flex flex-col gap-6">
      {section(
        "customizedSection",
        "wl-insurance-types-customized",
        customizedTypes
      )}
      {section(
        "defaultSection",
        "wl-insurance-types-default",
        defaultTypes
      )}
    </div>
  )
}
